import React from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useLanguage } from "context/LanguageContext";

function XmlExportSettingsDialog({
  open,
  onClose,
  onConfirm,
  xmlExportType,
  setXmlExportType,
  xmlExportWithMatches,
  selectedPozymiai,
  setSelectedPozymiai,
  includePvmInTotal,
  setIncludePvmInTotal,
  spacyModelSize,
  setSpacyModelSize,
  matcherType,
  setMatcherType,
  overridePvmCode,
  setOverridePvmCode,
  recalculateVat,
  setRecalculateVat,
  pvmCodeOptions,
  pozymiaiOptions,
}) {
  const { t } = useLanguage();

  const handleConfirm = () => {
    onConfirm();
  };

  const handlePozymiaiToggle = (pozymiusId) => {
    setSelectedPozymiai((prev) =>
      prev.includes(pozymiusId) ? prev.filter((id) => id !== pozymiusId) : [...prev, pozymiusId]
    );
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t("xmlExportSettings")}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t("selectXmlExportSettings")}</DialogContentText>

        <MDBox mt={2}>
          <MDTypography variant="body2" fontWeight="medium" mb={1}>
            {t("exportType")}
          </MDTypography>
          <RadioGroup value={xmlExportType} onChange={(e) => setXmlExportType(e.target.value)} row>
            <FormControlLabel value="agnum" control={<Radio />} label={t("purchases")} />
            <FormControlLabel value="sales" control={<Radio />} label={t("salesInvoices")} />
            <FormControlLabel value="agnum_summary" control={<Radio />} label={t("summary")} />
          </RadioGroup>
        </MDBox>

        <MDBox mt={2}>
          <MDTypography variant="body2" fontWeight="medium" mb={1}>
            {t("pvmCodeOverride")}
          </MDTypography>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>{t("selectPvmCode")}</InputLabel>
            <Select
              value={overridePvmCode}
              onChange={(e) => setOverridePvmCode(e.target.value)}
              label={t("selectPvmCode")}
            >
              {pvmCodeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <MDTypography variant="caption" color="text" mt={1} display="block">
            {t("pvmCodeOverrideDescription")}
          </MDTypography>
        </MDBox>

        <MDBox mt={3}>
          <MDTypography variant="body2" fontWeight="medium" mb={1}>
            Pozymiai
          </MDTypography>
          <FormGroup row>
            {pozymiaiOptions.map((pozymius) => (
              <FormControlLabel
                key={pozymius.id}
                control={
                  <Checkbox
                    checked={selectedPozymiai.includes(pozymius.id)}
                    onChange={() => handlePozymiaiToggle(pozymius.id)}
                    size="small"
                  />
                }
                label={pozymius.name}
                sx={{ minWidth: 120, margin: 0.5 }}
              />
            ))}
          </FormGroup>
        </MDBox>

        <MDBox mt={3}>
          <MDTypography variant="body2" fontWeight="medium" mb={1}>
            {t("includePvmInTotal")}
          </MDTypography>
          <RadioGroup
            value={includePvmInTotal ? "true" : "false"}
            onChange={(e) => setIncludePvmInTotal(e.target.value === "true")}
          >
            <FormControlLabel value="true" control={<Radio />} label={t("yes")} />
            <FormControlLabel value="false" control={<Radio />} label={t("no")} />
          </RadioGroup>
        </MDBox>

        <MDBox mt={3}>
          <MDTypography variant="body2" fontWeight="medium" mb={1}>
            {t("recalcVatTitle")}
          </MDTypography>
          <RadioGroup
            value={recalculateVat ? "true" : "false"}
            onChange={(e) => setRecalculateVat(e.target.value === "true")}
          >
            <FormControlLabel value="true" control={<Radio />} label={t("yes")} />
            <FormControlLabel value="false" control={<Radio />} label={t("no")} />
          </RadioGroup>
          <MDTypography variant="caption" color="text" mt={1} display="block">
            {t("recalcVatDescription")}
          </MDTypography>
        </MDBox>

        {xmlExportWithMatches && (
          <>
            <MDBox mt={3}>
              <MDTypography variant="body2" fontWeight="medium" mb={1}>
                {t("matcherType") || "Matching Method"}
              </MDTypography>
              <RadioGroup value={matcherType} onChange={(e) => setMatcherType(e.target.value)} row>
                <FormControlLabel value="spacy" control={<Radio />} label="spaCy / Fuzzy" />
                <FormControlLabel value="llm" control={<Radio />} label="LLM (AI)" />
              </RadioGroup>
            </MDBox>

            <MDBox mt={3}>
              <MDTypography variant="body2" fontWeight="medium" mb={1}>
                {t("spacyModelSize")}
              </MDTypography>
              <RadioGroup
                value={spacyModelSize}
                onChange={(e) => setSpacyModelSize(e.target.value)}
              >
                <FormControlLabel value="md" control={<Radio />} label={t("mediumModel")} />
                <FormControlLabel value="lg" control={<Radio />} label={t("largeModel")} />
              </RadioGroup>
              <MDTypography variant="caption" color="text" mt={1} display="block">
                {t("spacyModelDescription")}
              </MDTypography>
            </MDBox>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary">
          {t("cancel")}
        </MDButton>
        <MDButton onClick={handleConfirm} color="primary">
          {t("confirm")}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

XmlExportSettingsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  xmlExportType: PropTypes.string.isRequired,
  setXmlExportType: PropTypes.func.isRequired,
  xmlExportWithMatches: PropTypes.bool.isRequired,
  selectedPozymiai: PropTypes.array.isRequired,
  setSelectedPozymiai: PropTypes.func.isRequired,
  includePvmInTotal: PropTypes.bool.isRequired,
  setIncludePvmInTotal: PropTypes.func.isRequired,
  spacyModelSize: PropTypes.string.isRequired,
  setSpacyModelSize: PropTypes.func.isRequired,
  matcherType: PropTypes.string.isRequired,
  setMatcherType: PropTypes.func.isRequired,
  overridePvmCode: PropTypes.string.isRequired,
  setOverridePvmCode: PropTypes.func.isRequired,
  recalculateVat: PropTypes.bool.isRequired,
  setRecalculateVat: PropTypes.func.isRequired,
  pvmCodeOptions: PropTypes.array.isRequired,
  pozymiaiOptions: PropTypes.array.isRequired,
};

export default XmlExportSettingsDialog;
